import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { startDiscordConnect } from "@/lib/discordConnect";
import { useTranslation } from "react-i18next";
import { Flame, Trophy, ChevronRight, Lock, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { API_BASE } from "@/lib/api";

/**
 * Dashboard achievement + streak widget.
 * Renders:
 *  • Login streak count with fire icon
 *  • Grid of 6 most-recently-unlocked badges (real PNG art)
 *  • A subtle "N locked" pill that links to the full Achievements page
 */
export default function AchievementsWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [streak, setStreak] = useState(null);
  const [earned, setEarned] = useState([]);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, e, c] = await Promise.all([
          api.get("/achievements/streak"),
          api.get("/achievements/me"),
          api.get("/achievements/catalog"),
        ]);
        if (!alive) return;
        setStreak(s.data);
        setEarned(e.data);
        setCatalog(c.data);
      } catch (_e) {
        // Non-fatal — widget just doesn't render
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!catalog.length) return null;

  const earnedMap = new Map(earned.map((e) => [e.kind, e.earned_at]));
  // Show 6 badges: earned first (most recent), then locked fillers from catalog
  const earnedKinds = new Set(earnedMap.keys());
  const recentEarned = earned
    .slice(0, 6)
    .map((e) => ({ ...catalog.find((c) => c.kind === e.kind), earned_at: e.earned_at }))
    .filter(Boolean);
  const showDiscordUnlock = !user?.discord_linked && !earnedKinds.has("icebreaker");
  const lockedCount = Math.max(showDiscordUnlock ? 1 : 0, 6 - recentEarned.length);
  const locked = catalog
    .filter((c) => !earnedKinds.has(c.kind))
    .sort((a, b) => (showDiscordUnlock ? (b.kind === "icebreaker") - (a.kind === "icebreaker") : 0))
    .slice(0, lockedCount);
  const display = [...recentEarned.slice(0, 6 - locked.length), ...locked];

  const totalEarned = earned.length;
  const totalCatalog = catalog.length;

  return (
    <Card data-testid="dashboard-achievements-widget" className="bg-white border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-display font-bold tracking-tight text-slate-900">
            {t("achievementsWidget.title")}
          </CardTitle>
          <Link
            to="/achievements"
            data-testid="dashboard-achievements-see-all"
            className="text-xs font-semibold text-amber-700 hover:text-amber-600 inline-flex items-center gap-0.5"
          >
            {t("achievementsWidget.seeAll")} <ChevronRight size={12} />
          </Link>
        </div>
        {/* Streak line */}
        {streak && (
          <div className="mt-2 flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1">
              <Flame size={13} className="text-orange-500" />
              <span data-testid="dashboard-streak-value" className="text-[11px] font-bold text-orange-800 uppercase tracking-wider">
                {t("achievementsWidget.streak", { count: streak.login_streak })}
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <Trophy size={12} className="text-slate-400" />
              {t("achievementsWidget.unlocked", { earned: totalEarned, total: totalCatalog })}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-6 gap-2">
          {display.map((b) => {
            const isEarned = earnedKinds.has(b.kind);
            return (
              <div
                key={b.kind}
                data-testid={`badge-${b.kind}`}
                title={`${b.title} — ${b.description}${isEarned ? "" : ` (${t("achievementsWidget.locked")})`}`}
                className={`relative aspect-square rounded-lg flex items-center justify-center p-1.5 ${
                  isEarned
                    ? "bg-gradient-to-br from-amber-50 to-white border border-amber-200"
                    : "bg-slate-50 border border-slate-200"
                }`}
              >
                <img
                  src={`${API_BASE}/badges/${b.kind}.png?v=2`}
                  alt={b.title}
                  className={`w-full h-full object-contain ${isEarned ? "" : "grayscale opacity-30"}`}
                />
                {!isEarned && (
                  <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-white/95 shadow-sm flex items-center justify-center">
                    <Lock size={9} className="text-slate-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {showDiscordUnlock && (
          <button
            type="button"
            onClick={() => startDiscordConnect(navigate)}
            data-testid="dashboard-achievements-discord-unlock"
            className="mt-3 w-full flex items-center gap-3 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/[0.06] hover:bg-[#5865F2]/10 px-3 py-2 text-left transition-colors"
          >
            <MessageSquare size={14} className="text-[#5865F2] shrink-0" />
            <span className="flex-1 text-xs text-slate-700">
              <span className="font-semibold text-slate-900">Discord Insider</span> · {t("achievementsWidget.discordUnlock", { defaultValue: "Connect Discord to unlock" })}
            </span>
            <ChevronRight size={14} className="text-[#5865F2]" />
          </button>
        )}
      </CardContent>
    </Card>
  );
}
